import { getNumbers, useTokenValues } from '@jds/react-engine';
import { useMemo, useState } from 'react';

import { JDSRatingBarProps } from './generated/interface';
import { useSelected } from '../../hooks/useSelected';

export interface RatingBarProps
  extends Omit<JDSRatingBarProps, 'onChange' | 'count'> {
  onChange?: (rating: number) => void;
  count?: number;
  ariaLabel?: string;
  ariaDescribedby?: string;
}

const EPSILON = 0.005;

export const withLogic =
  (Component: React.ComponentType<RatingBarProps>): React.FC<RatingBarProps> =>
  ({ rating = -1, ...props }: RatingBarProps) => {
    const { index: userRating, updateIndex } = useSelected(rating);
    const ratingIsInt = Math.abs(Math.floor(userRating) - userRating) < EPSILON;
    const [hoverIndex, setHoverIndex] = useState(-1);

    const decimalPart = Math.abs(Math.floor(userRating) - userRating);

    const tokens = useTokenValues();
    const language = tokens.lang;
    const numeralList = useMemo(() => {
      return getNumbers(language);
    }, [language]);

    const getLocalNumber = (number: number) => {
      const numString = number.toString();
      return numString.replace(
        /\d/g,
        (num) => numeralList[parseInt(num, 10)] ?? num
      );
    };
    const roundedUserRating = Math.round(userRating);

    return (
      <Component
        {...{
          ...props,
          rating: userRating,
          _role: props.type === 'interactive' ? 'radiogroup' : 'img',
          ariaLabel:
            props.type === 'interactive'
              ? `${roundedUserRating} star`
              : `Rated ${roundedUserRating} out of ${props.count} star`,
          _label:
            props.type === 'read_only_compact'
              ? getLocalNumber(rating)
              : props.label,
          _onHover: (idx: number) => {
            setHoverIndex(idx);
          },
          _onHoverEnd: (idx: number) => {
            setHoverIndex(idx);
          },
          _items: Array.from({ length: props.count || 0 }).map((_, i) => {
            const isHalfRated = !ratingIsInt && i + 1 === Math.ceil(userRating);
            return {
              isInteractive: props.type === 'interactive',
              itemAriaLabel:
                props.type === 'interactive' ? `${i + 1} star` : undefined,
              itemAriaChecked:
                props.type === 'interactive'
                  ? userRating === i + 1
                    ? 'true'
                    : 'false'
                  : undefined,
              isActive:
                hoverIndex === -1 ? i + 1 <= userRating : i <= hoverIndex,
              isHalfRated,
              rating: isHalfRated ? 100 * (1 - decimalPart) + '%' : undefined,
              onChange: (index: number) => {
                if (props.type === 'interactive') {
                  updateIndex(index + 1);
                  props.onChange?.(index + 1);
                }
              }
            };
          })
        }}
      />
    );
  };
